import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BrandConfig, BRAND_CONFIG } from '../config/brand.config';

/**
 * Serviço de Branding Whitelabel
 *
 * Gerencia as configurações de marca da aplicação de forma centralizada.
 * Permite troca dinâmica de marca em runtime se necessário.
 */
@Injectable({
  providedIn: 'root',
})
export class BrandService {
  private brandConfig$ = new BehaviorSubject<BrandConfig>(BRAND_CONFIG);

  /**
   * Observable com a configuração atual da marca
   */
  get config$(): Observable<BrandConfig> {
    return this.brandConfig$.asObservable();
  }

  /**
   * Retorna a configuração atual da marca
   */
  get config(): BrandConfig {
    return this.brandConfig$.value;
  }

  /**
   * Atualiza a configuração da marca (útil para troca dinâmica)
   */
  setBrandConfig(config: BrandConfig): void {
    this.brandConfig$.next(config);
    this.updateDocumentMeta(config);
  }

  /**
   * Atualiza o título da página
   */
  setPageTitle(title: string): void {
    const config = this.config;
    document.title = `${title} | ${config.appName}`;
  }

  /**
   * Retorna uma cor do tema
   */
  getThemeColor(colorKey: keyof BrandConfig['theme']): string {
    return this.config.theme[colorKey];
  }

  /**
   * Inicializa as configurações da marca no documento
   * Deve ser chamado no AppComponent.ngOnInit()
   */
  initializeBrand(): void {
    this.updateDocumentMeta(this.config);
  }

  /**
   * Atualiza as meta tags do documento
   */
  private updateDocumentMeta(config: BrandConfig): void {
    document.title = config.meta.title;

    this.updateMetaTag('description', config.meta.description);
    this.updateMetaTag('keywords', config.meta.keywords);

    const favicon = document.querySelector(
      "link[rel*='icon']",
    ) as HTMLLinkElement;
    if (favicon) {
      favicon.href = config.favicon;
    }
  }

  /**
   * Helper para atualizar meta tag
   */
  private updateMetaTag(name: string, content: string): void {
    let meta = document.querySelector(
      `meta[name="${name}"]`,
    ) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  /**
   * Retorna as iniciais de um nome
   */
  getInitials(fullName: string): string {
    return fullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }
}
